import { parseColor } from "@/document/model/color";
import {
  createBackgroundBlurEffect,
  type BackgroundBlurEffect,
  type InnerShadowEffect,
  type NodeEffect,
} from "@/document/model/styles";
import { createId } from "@/lib/ids";
import type { SvgDefsRegistry } from "./svgDefs";
import type { SvgImportIssueCollector } from "./issues";
import { elementLocalName, readSvgLength } from "./parseSvgDocument";

export function parseCurvaEffectsAttribute(
  raw: string | null | undefined,
  issues: SvgImportIssueCollector,
): NodeEffect[] {
  if (!raw?.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as NodeEffect[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map(effect => normalizeImportedEffect(effect));
  } catch {
    issues.addOnce("unsupported-attribute", "Could not parse data-curva-effects metadata", "data-curva-effects");
    return [];
  }
}

export function resolveElementEffects(
  element: Element,
  defs: SvgDefsRegistry,
  issues: SvgImportIssueCollector,
): NodeEffect[] {
  const metadata = parseCurvaEffectsAttribute(element.getAttribute("data-curva-effects"), issues);
  if (metadata.length > 0) {
    return metadata;
  }

  const filter = element.getAttribute("filter");
  if (filter) {
    return defs.resolveFilterEffects(filter, issues);
  }

  return [];
}

export function parseBackgroundBlurFromForeignObject(element: Element): BackgroundBlurEffect | undefined {
  if (elementLocalName(element) !== "foreignobject") {
    return undefined;
  }

  const radius = readSvgLength(element.getAttribute("data-curva-bg-blur-radius"));
  if (radius <= 0) {
    const style = element.getAttribute("style") ?? "";
    const match = style.match(/backdrop-filter:\s*blur\(([\d.]+)px\)/i);
    if (!match) {
      return undefined;
    }
    return { ...createBackgroundBlurEffect(), blur: Number.parseFloat(match[1]!) };
  }

  return { ...createBackgroundBlurEffect(), blur: radius };
}

function normalizeImportedEffect(effect: NodeEffect): NodeEffect {
  const id = effect.id || createId("effect");
  if (effect.type === "dropShadow" || effect.type === "innerShadow") {
    return {
      ...effect,
      id,
      color: parseColor(effect.color as unknown as string),
    } as InnerShadowEffect;
  }
  return { ...effect, id };
}
